/**
 * App.UI - DOM 面板 / 指标 / 模式 / 绑定
 */

import type { RouteResult } from '../types';
import { App } from './App';

/* ─────────────── Module: UI（DOM 面板 / 指标 / 模式 / 绑定）─────────────── */
App.UI = {
  /** 各模式的提示文本 */
  HINT_EDIT: '编辑模式（当前层可编辑）：左键旋转 · 右键平移 · 滚轮缩放 · 点击格子切换 走管/禁行',
  HINT_LINE: '连线模式：单击添加顶点 · 双击/回车 完成当前线 · Esc 取消 · 可连续定义多条线（相交处自动交接）',
  HINT_END: '末端模式：单击空地 添加末端 · 拖拽末端 移动 · 单击末端 删除 · 左键拖拽空白处仍可旋转',
  HINT_ROOM: '机房模式：单击空地 添加机房(2×2) · 拖拽机房 移动 · 单击机房 删除 · 多机房自动多源寻路',
  HINT_SHAFT: '竖井模式：单击格子放置 贯通竖井（自动连接所有楼层）· 再点同位置移除 · 竖井是跨层寻路的垂直通道',
  HINT_PROP: '属性模式：点击 机房 / 末端 / 格子 查看属性；机房·末端可改颜色（可撤销）',
  HINT_PLAN: '平面图视图（当前层）：按所选模式单击格子编辑（机房/连线/末端/竖井）· 右键拖拽平移 · 滚轮缩放 · 点左上「3D 透视」切回',

  /**
   * 设置提示文本
   */
  setHint(t: string): void {
    const el = document.getElementById('hint');
    if (el) el.textContent = t;
  },

  /**
   * 设置进度文本
   */
  setProgress(t: string): void {
    const el = document.getElementById('progTxt');
    if (el) el.textContent = t;
  },

  /**
   * 切换视图模式（3D透视 ↔ 平面图）
   */
  setView(plan: boolean): void {
    const S = App.Store;
    const RR = App.Render;

    if (S.planView === plan) return;

    RR.setPlanView(plan);

    const btn3D = document.getElementById('btnView3D');
    const btnPlan = document.getElementById('btnViewPlan');
    const planBadge = document.getElementById('planBadge');

    if (btn3D) btn3D.classList.toggle('active', !plan);
    if (btnPlan) btnPlan.classList.toggle('active', plan);
    if (planBadge) (planBadge as HTMLElement).style.display = plan ? 'block' : 'none';

    this.updatePlanBadge();
    this.renderFloorTabs();

    if (plan) {
      this.setHint(this.HINT_PLAN);
    } else {
      this.updateModeUI();
    }

    this.setProgress(
      plan
        ? `已切换到平面图视图 · 当前层 ${S.f?.name || ''} · 单击格子按当前模式编辑`
        : '已切换到 3D 透视视图'
    );
  },

  /**
   * 更新平面图标记
   */
  updatePlanBadge(): void {
    const el = document.getElementById('planBadgeName');
    if (el) el.textContent = App.Store.f?.name || '一层';
  },

  /**
   * 设置操作模式
   */
  setMode(m: string): void {
    const S = App.Store;
    const RR = App.Render;

    if (S.running || m === S.mode) return;

    S.mode = m;

    if (m !== 'line') S.curLine = [];
    if (m !== 'end' && m !== 'room') {
      S.dragEnd = null;
      S.dragRoom = null;
      S.downCell = null;
      if (RR.activeControls) (RR.activeControls() as any).enabled = true;
    }
    if (m !== 'prop') (App.Selection as any).clear();

    this.updateModeUI();
    this.updateDrawStatus();

    const msg =
      m === 'line'
        ? '已进入连线模式 · 单击网格添加顶点，画出走管通道'
        : m === 'end'
        ? '已进入末端模式 · 单击空地添加 · 拖拽移动 · 单击末端删除'
        : m === 'room'
        ? '已进入机房模式 · 单击空地添加 · 拖拽移动 · 单击机房删除'
        : m === 'shaft'
        ? '已进入竖井模式 · 单击格子放置/移除贯通竖井'
        : m === 'prop'
        ? '已进入属性模式 · 点击机房 / 末端 / 格子查看属性'
        : '已进入编辑模式 · 点击格子切换 走管/禁行';

    this.setProgress(msg);
  },

  /**
   * 更新模式按钮UI状态
   */
  updateModeUI(): void {
    const S = App.Store;
    const RR = App.Render;

    const modeIds = ['modeEdit', 'modeLine', 'modeEnd', 'modeRoom', 'modeShaft', 'modeProp'];
    const modes = ['edit', 'line', 'end', 'room', 'shaft', 'prop'];

    modeIds.forEach((id, i) => {
      const btn = document.getElementById(id);
      if (btn) btn.classList.toggle('active', S.mode === modes[i]);
    });

    const drawTools = document.getElementById('drawTools');
    const endTools = document.getElementById('endTools');
    const roomTools = document.getElementById('roomTools');
    const shaftTools = document.getElementById('shaftTools');

    if (drawTools) drawTools.style.display = S.mode === 'line' ? 'block' : 'none';
    if (endTools) endTools.style.display = S.mode === 'end' ? 'block' : 'none';
    if (roomTools) roomTools.style.display = S.mode === 'room' ? 'block' : 'none';
    if (shaftTools) shaftTools.style.display = S.mode === 'shaft' ? 'block' : 'none';

    const hints: Record<string, string> = {
      line: this.HINT_LINE,
      end: this.HINT_END,
      room: this.HINT_ROOM,
      shaft: this.HINT_SHAFT,
      prop: this.HINT_PROP,
      edit: this.HINT_EDIT,
    };
    this.setHint(hints[S.mode] || this.HINT_EDIT);

    if (S.mode !== 'line' && RR.hoverMesh) RR.hoverMesh.visible = false;
    RR.buildPreview();
    this.renderBindUI();
  },

  /**
   * 更新绘制状态文本
   */
  updateDrawStatus(): void {
    const S = App.Store;
    const f = S.f;

    const drawStatus = document.getElementById('drawStatus');
    const endStatus = document.getElementById('endStatus');
    const roomStatus = document.getElementById('roomStatus');
    const shaftStatus = document.getElementById('shaftStatus');

    if (drawStatus) {
      drawStatus.innerHTML = `线条 <b>${f?.lines.length || 0}</b> · 交接 <b>${f?.junctions.length || 0}</b> · 当前顶点 <b>${S.curLine.length}</b>`;
    }

    if (endStatus && f) {
      const addedE = f.ends.filter(e => e.added).length;
      endStatus.innerHTML = `当前层 <b>${f.ends.length}</b> 个末端（自建 <b>${addedE}</b>）`;
    }

    if (roomStatus && f) {
      const addedR = f.rooms.filter(r => r.added).length;
      roomStatus.innerHTML = `当前层 <b>${f.rooms.length}</b> 个机房（自建 <b>${addedR}</b>）`;
    }

    if (shaftStatus) {
      const shafts = S.shafts;
      if (!shafts.length) {
        shaftStatus.innerHTML = `竖井 <b>0</b> 处 · 当前层 ${S.f?.name || ''}`;
      } else {
        const range = (s: any) => {
          const a = S.floors[s.floorFrom]?.name || '?';
          const b = S.floors[s.floorTo]?.name || '?';
          return s.floorFrom === s.floorTo ? a : `${a}~${b}`;
        };
        const list = shafts.map((s: any) => `井${s.id}:${range(s)}`).join(' ');
        shaftStatus.innerHTML = `竖井 <b>${shafts.length}</b> 处 · ${list}`;
      }
    }
  },

  /**
   * 刷新指标面板
   */
  refreshMetrics(tAll: number): void {
    const S = App.Store;
    const ok = S.results.filter(r => r.ok);
    const totalEnds = (App.Routing as any).allEnds().length;

    const mConn = document.getElementById('mConn');
    const mTime = document.getElementById('mTime');
    const mLen = document.getElementById('mLen');
    const mAvg = document.getElementById('mAvg');

    // 显示路径数量/末端数量
    if (mConn) mConn.textContent = `${ok.length}条路径/${totalEnds}末端`;
    if (mTime) mTime.textContent = tAll.toFixed(2);

    const total = S.results.reduce((s: number, r: RouteResult) => s + r.len, 0);
    if (mLen) mLen.textContent = total.toString();
    if (mAvg) mAvg.textContent = ok.length ? (total / ok.length).toFixed(1) : '0';

    // 更新明细表
    const tb = document.getElementById('tblBody');
    if (!tb) return;

    tb.innerHTML = '';
    for (const r of S.results) {
      const fromCell = S.bindMode
        ? r.from || (r.reason === '未绑定机房' ? '未绑定' : '—')
        : r.from || '—';
      const fn = (App.World as any).floorName(r.end.floorIdx);

      const hex = '#' + (r.end.color as number).toString(16).padStart(6, '0');

      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>
          <span class="swatch" style="background:${hex}"></span> ${r.end.id}
          <div style="font-size:9px;color:var(--mut)">${fn}</div>
        </td>
        <td>${fromCell}</td>
        <td class="num">${r.ok ? r.len : '—'}</td>
        <td class="num">${r.ok ? r.cor : '—'}</td>
        <td>${r.ok ? '✓ 连通' : r.reason === '未绑定机房' ? '× 未绑定' : '× 隔离'}</td>
      `;
      tb.appendChild(tr);
    }
  },

  /**
   * 渲染顶部楼层标签页
   */
  renderFloorTabs(): void {
    const S = App.Store;
    const wrap = document.getElementById('floorTabs');
    if (!wrap) return;

    let html = '';
    S.floors.forEach((f, i) => {
      const isActive = i === S.activeFloorIdx && S.planView;
      const nRooms = f.rooms.length;
      const nEnds = f.ends.length;

      html += `<div class="floor-tab${isActive ? ' active' : ''}" data-fi="${i}">
        <span class="fdot"></span>
        <span class="fname">${f.name}</span>
        <span class="fmeta">机房${nRooms}·末端${nEnds}</span>
      </div>`;
    });

    wrap.innerHTML = html;

    wrap.querySelectorAll('.floor-tab').forEach((el: Element) => {
      const tab = el as HTMLDivElement;
      tab.onclick = () => {
        const fi = +tab.dataset.fi!;
        const I = App.Interaction as any;
        const UI = App.UI;

        // 如果已在平面视图且点击的是当前楼层，则切回 3D 透视
        if (S.planView && fi === S.activeFloorIdx) {
          UI.setView(false);
          return;
        }

        // 切换到目标楼层
        if (fi !== S.activeFloorIdx) {
          I.switchFloor(fi);
        }

        // 进入平面视图
        if (!S.planView) {
          UI.setView(true);
        } else {
          // 已在平面视图但切换了楼层，更新标签
          UI.renderFloorTabs();
        }
      };
    });
  },

  /**
   * 渲染楼层管理面板
   */
  renderFloorPanel(): void {
    const S = App.Store;
    const wrap = document.getElementById('floorPanel');
    if (!wrap) return;

    let html = '';
    S.floors.forEach((f, i) => {
      const act = i === S.activeFloorIdx;
      const nRooms = f.rooms.length;
      const nEnds = f.ends.length;

      html += `<div class="floor-row${act ? ' active' : ''}" data-fi="${i}">
        <span class="fdot"></span>
        <span class="fname">${f.name}${act ? ' <span style="color:#9fd2ff;font-size:10px">·编辑中</span>' : ''}</span>
        <span class="fmeta">机房${nRooms}·末端${nEnds}</span>
      </div>`;
    });

    wrap.innerHTML = html;

    wrap.querySelectorAll('.floor-row').forEach((row: Element) => {
      const div = row as HTMLDivElement;
      div.onclick = () => (App.Interaction as any).switchFloor(+div.dataset.fi!);
    });
  },

  /**
   * 渲染绑定UI
   */
  renderBindUI(): void {
    const S = App.Store;
    const wrap = document.getElementById('bindWrap');
    if (!wrap) return;

    if (!S.bindMode) {
      wrap.style.display = 'none';
      wrap.innerHTML = '';
      return;
    }

    wrap.style.display = 'block';

    let html = '<div class="bind-hint">勾选每个机房要连接的末端（仅同专业、可跨楼层）；机房只连通同专业末端，生成多条路径。</div>';

    const allRooms: Array<{ r: any; f: any }> = [];
    const allEnds: Array<{ e: any; f: any }> = [];

    S.floors.forEach((f, fi) => {
      f.rooms.forEach((r: any) => allRooms.push({ r, f }));
      f.ends.forEach((e: any) => allEnds.push({ e, f }));
    });

    for (const { r, f } of allRooms) {
      const set = (App.Routing as any).boundSetFor(r.id);
      const hex = '#' + (r.color as number).toString(16).padStart(6, '0');
      const sysName = (App.Routing as any).systemNameOf(r.systemId);

      // 仅列出与该机房同专业的末端
      const sameSysEnds = allEnds.filter(({ e }) => e.systemId === r.systemId);

      html += `<div class="bind-room">
        <div class="rh">
          <span class="swatch" style="background:${hex}"></span>${r.id}
          <span style="color:var(--mut);font-size:10px">(${f.name} · ${sysName})</span>
        </div>
        <div class="bind-grid">`;

      if (!sameSysEnds.length) {
        html += `<div style="color:var(--mut);font-size:10px;padding:4px 0">无同专业（${sysName}）末端</div>`;
      }

      for (const { e, f: ef } of sameSysEnds) {
        const ck = set.has(e.id) ? 'checked' : '';
        html += `<label>
          <input type="checkbox" data-rid="${r.id}" data-eid="${e.id}" ${ck}>
          ${e.id}<span style="color:var(--mut);font-size:9px">${ef.name}</span>
        </label>`;
      }

      html += '</div></div>';
    }

    wrap.innerHTML = html;

    wrap.querySelectorAll('input[type="checkbox"]').forEach((cb: Element) => {
      const checkbox = cb as HTMLInputElement;
      checkbox.onchange = () => {
        (App.Interaction as any).setBind(checkbox.dataset.rid, checkbox.dataset.eid, checkbox.checked);
      };
    });
  },

  /**
   * 切换绑定模式
   */
  toggleBindMode(on: boolean): void {
    (App.Interaction as any).setBindMode(on);
  },

  /**
   * 渲染管线系统管理面板
   */
  renderSystemPanel(): void {
    const S = App.Store;
    const wrap = document.getElementById('systemPanel');
    if (!wrap) return;

    let html = '';
    S.systems.forEach((s: any) => {
      const hex = '#' + (s.color as number).toString(16).padStart(6, '0');
      html += `<div class="sys-row">
        <input type="color" class="sys-color" data-sid="${s.id}" value="${hex}" title="系统颜色">
        <input type="text" class="sys-abbr" data-sid="${s.id}" value="${s.abbr || ''}" maxlength="3" title="简称（水/暖/电，显示在机房末端名后）">
        <input type="text" class="sys-name" data-sid="${s.id}" value="${s.name}" title="系统名称">
      </div>`;
    });
    html += `<button class="btn" id="btnAddSystem" style="margin-top:6px"><span class="ic">＋</span>新增管线系统</button>`;

    wrap.innerHTML = html;

    wrap.querySelectorAll('.sys-color').forEach((el: Element) => {
      (el as HTMLInputElement).onchange = (ev: Event) => {
        const t = ev.target as HTMLInputElement;
        const sid = +(t.dataset as any).sid;
        const sys = S.systems.find((s: any) => s.id === sid);
        if (sys) (App.Interaction as any).setSystemProp(sid, sys.name, parseInt(t.value.replace('#', ''), 16), sys.abbr);
      };
    });

    wrap.querySelectorAll('.sys-name').forEach((el: Element) => {
      (el as HTMLInputElement).onchange = (ev: Event) => {
        const t = ev.target as HTMLInputElement;
        const sid = +(t.dataset as any).sid;
        const sys = S.systems.find((s: any) => s.id === sid);
        if (sys) (App.Interaction as any).setSystemProp(sid, t.value || sys.name, sys.color, sys.abbr);
      };
    });

    wrap.querySelectorAll('.sys-abbr').forEach((el: Element) => {
      (el as HTMLInputElement).onchange = (ev: Event) => {
        const t = ev.target as HTMLInputElement;
        const sid = +(t.dataset as any).sid;
        const sys = S.systems.find((s: any) => s.id === sid);
        if (sys) (App.Interaction as any).setSystemProp(sid, sys.name, sys.color, t.value);
      };
    });

    const addBtn = document.getElementById('btnAddSystem');
    if (addBtn) addBtn.onclick = () => (App.Interaction as any).addSystem();
  },

  /**
   * 渲染机房/末端模式的「添加归属系统」选择器（共享 activeSystemId，保持同步）
   */
  renderRoomSystemPick(): void {
    this._renderSystemPick('roomSystemPick', '添加机房归属系统：');
    this._renderSystemPick('endSystemPick', '添加末端归属系统：');
    this._renderSystemPick('shaftSystemPick', '添加竖井归属系统：');
  },

  _renderSystemPick(wrapId: string, hint: string): void {
    const S = App.Store;
    const wrap = document.getElementById(wrapId);
    if (!wrap) return;

    let html = `<div class="sysPickHint">${hint}</div><div class="sysPickRow">`;
    S.systems.forEach((s: any) => {
      const hex = '#' + (s.color as number).toString(16).padStart(6, '0');
      const act = s.id === S.activeSystemId;
      html += `<button type="button" class="sysPickBtn${act ? ' active' : ''}" data-sid="${s.id}" style="--sc:${hex}">
        <span class="swatch" style="background:${hex}"></span>${s.name}
      </button>`;
    });
    html += '</div>';

    wrap.innerHTML = html;

    wrap.querySelectorAll('.sysPickBtn').forEach((el: Element) => {
      const btn = el as HTMLButtonElement;
      btn.onclick = () => {
        S.activeSystemId = +((btn.dataset as any).sid);
        this.renderRoomSystemPick(); // 刷新机房+末端两个选择器，保持同步
      };
    });
  },

  /**
   * 渲染末端批量管理面板（跨楼层列出所有末端，可勾选 + 批量改系统）
   */
  renderEndPanel(): void {
    const S = App.Store;
    const wrap = document.getElementById('endPanel');
    if (!wrap) return;

    const allEnds: Array<{ e: any; f: any }> = [];
    S.floors.forEach((f: any) => f.ends.forEach((e: any) => allEnds.push({ e, f })));

    const sysOpts = S.systems
      .map((s: any) => `<option value="${s.id}">${s.name}</option>`)
      .join('');

    const allChecked = allEnds.length > 0 && allEnds.every(({ e }) => S.selectedEndIds.has(e.id));
    const someChecked = S.selectedEndIds.size > 0;

    let html = `<div class="endPanelTools">
      <label class="chk" style="margin:0"><input type="checkbox" id="endSelAll" ${allChecked ? 'checked' : ''}> 全选</label>
      <select id="endBatchSys" class="sys-name" style="flex:1;min-width:0">${sysOpts}</select>
      <button type="button" class="btn" id="btnEndBatch" style="margin:0;padding:6px 10px;${someChecked ? '' : 'opacity:.5'}">设系统</button>
    </div>`;

    html += '<div class="endList">';
    allEnds.forEach(({ e, f }) => {
      const checked = S.selectedEndIds.has(e.id);
      const sys = (App.Routing as any).systemOf(e.systemId);
      const hex = sys ? '#' + (sys.color as number).toString(16).padStart(6, '0') : '#888';
      const sysName = sys ? sys.name : '未分类';
      const abbr = (App.Routing as any).systemAbbrOf(e.systemId);
      html += `<div class="endRow${checked ? ' selected' : ''}">
        <input type="checkbox" class="endChk" data-eid="${e.id}" ${checked ? 'checked' : ''}>
        <span class="swatch" style="background:${hex}" title="${sysName}"></span>
        <span class="endName">${e.id}${abbr ? '(' + abbr + ')' : ''}</span>
        <span class="endMeta">${f.name} · ${sysName}</span>
      </div>`;
    });
    html += '</div>';

    wrap.innerHTML = html;

    // 单个复选框：仅更新选中态与行样式，不重建整面板
    wrap.querySelectorAll('.endChk').forEach((el: Element) => {
      const cb = el as HTMLInputElement;
      cb.onchange = () => {
        const eid = (cb.dataset as any).eid;
        if (cb.checked) S.selectedEndIds.add(eid);
        else S.selectedEndIds.delete(eid);
        const row = cb.closest('.endRow') as HTMLElement | null;
        if (row) row.classList.toggle('selected', cb.checked);
        // 同步全选与按钮可用态
        const all = document.getElementById('endSelAll') as HTMLInputElement | null;
        const btn = document.getElementById('btnEndBatch') as HTMLButtonElement | null;
        const cnt = S.selectedEndIds.size;
        if (all) all.checked = cnt > 0 && cnt === allEnds.length;
        if (btn) btn.style.opacity = cnt > 0 ? '1' : '.5';
      };
    });

    // 全选
    const all = document.getElementById('endSelAll') as HTMLInputElement | null;
    if (all) {
      all.onchange = () => {
        if (all.checked) allEnds.forEach(({ e }) => S.selectedEndIds.add(e.id));
        else S.selectedEndIds.clear();
        this.renderEndPanel();
      };
    }

    // 批量设系统
    const btn = document.getElementById('btnEndBatch') as HTMLButtonElement | null;
    if (btn) {
      btn.onclick = () => {
        const sel = document.getElementById('endBatchSys') as HTMLSelectElement | null;
        if (!sel) return;
        const ids = [...S.selectedEndIds];
        if (!ids.length) {
          this.setProgress('未选中任何末端');
          return;
        }
        (App.Interaction as any).setEndsSystem(ids, +sel.value);
      };
    }
  },

  /**
   * 渲染批量重命名工具区（按 系统/楼层 筛选，前缀+起始号顺号重命名）
   */
  renderRenamePanel(): void {
    const S = App.Store;
    const wrap = document.getElementById('renameTools');
    if (!wrap) return;

    const sysOpts = '<option value="all">全部系统</option>' +
      S.systems.map((s: any) => `<option value="${s.id}">${s.name}</option>`).join('');
    const floorOpts = '<option value="all">全部楼层</option>' +
      S.floors.map((f: any, i: number) => `<option value="${i}">${f.name}</option>`).join('');

    wrap.innerHTML =
      `<div class="renameHint">按筛选条件把机房/末端顺号重命名（如「给水末端1/2/3」）</div>` +
      `<div class="renameRow">
        <select id="renameKind"><option value="end">末端</option><option value="room">机房</option></select>
        <select id="renameSys">${sysOpts}</select>
      </div>` +
      `<div class="renameRow">
        <select id="renameFloor">${floorOpts}</select>
        <input id="renameStart" type="number" value="1" min="0" title="起始编号">
      </div>` +
      `<div class="renameRow">
        <input id="renamePrefix" placeholder="名称前缀" value="末端">
      </div>` +
      `<button class="btn primary" id="btnRename"><span class="ic">✎</span>批量重命名</button>`;

    const btn = document.getElementById('btnRename');
    if (btn) {
      btn.onclick = () => {
        const kind = (document.getElementById('renameKind') as HTMLSelectElement).value;
        const sys = (document.getElementById('renameSys') as HTMLSelectElement).value;
        const floor = (document.getElementById('renameFloor') as HTMLSelectElement).value;
        const prefix = (document.getElementById('renamePrefix') as HTMLInputElement).value.trim();
        const start = parseInt((document.getElementById('renameStart') as HTMLInputElement).value) || 1;
        if (!prefix) {
          this.setProgress('请输入名称前缀');
          return;
        }
        (App.Interaction as any).setBatchRename(kind, sys, floor, prefix, start);
      };
    }
  },

  /**
   * 渲染图例
   */
  legend(): void {
    const lg = document.getElementById('legend');
    if (!lg) return;

    lg.innerHTML = '<b style="color:var(--txt)">图例</b>';

    const w = document.createElement('div');
    w.style.cssText = 'display:flex;flex-direction:column;gap:4px;margin-top:6px';

    const items: [string, string][] = [
      ['可走管空间', '#f59e3b'],
      ['禁行区', '#5a6699'],
      ['竖井（跨层立管）', '#2ecc71'],
      ['走管线交接点', '#ffffff'],
      ['连线预览', '#3df0ff'],
    ];

    for (const [name, col] of items) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:6px';
      row.innerHTML = `<span class="swatch" style="background:${col}"></span>${name}`;
      w.appendChild(row);
    }

    // 管线系统颜色（机房/末端/路径按系统着色）
    const sysTitle = document.createElement('div');
    sysTitle.style.cssText = 'margin-top:6px;color:var(--mut);font-size:10px';
    sysTitle.textContent = '管线系统（机房/末端/路径着色）';
    w.appendChild(sysTitle);

    for (const s of App.Store.systems) {
      const hex = '#' + (s.color as number).toString(16).padStart(6, '0');
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:6px';
      row.innerHTML = `<span class="swatch" style="background:${hex}"></span>${s.name}`;
      w.appendChild(row);
    }

    lg.appendChild(w);
  },

  /**
   * 更新撤销/重做按钮状态
   */
  updateUndoRedoButtons(): void {
    const u = document.getElementById('btnUndo');
    const r = document.getElementById('btnRedo');
    const Command = App.Command as any;

    if (u) u.disabled = Command.undoStack.length === 0;
    if (r) r.disabled = Command.redoStack.length === 0;
  },
};

export default App.UI;
export const UI = App.UI;
